import React, { useMemo } from "react";
import BaseTable from "./BaseTable";
import { buildTable } from "../utils/tableBuilder";
import { generateDynamicColumns } from "../config/tableColumns";
import { TableProvider } from "../hooks/useTableContext";
import useProcessedTableData from "../hooks/useProcessedTableData";
import { formatBaseRecruitData } from "../utils/tableDataFormatters";

/**
 * Format data for the table from API response
 * @param {Array} recruits - Recruits data from API
 * @param {Object} formulasMapping - Mapping of formula data
 * @returns {Array} Formatted data for the table
 */
const formatAnalyticsTableData = (recruits, formulasMapping = {}) => {
  return recruits.map((recruit) => {
    const formulas = formulasMapping[recruit.id];
    const baseData = formatBaseRecruitData(recruit, formulas);

    const analyticsSpecificData = {
      // Store divisors for parameter fields
      P01divisor: recruit.P01divisor || 1,
      P02divisor: recruit.P02divisor || 1,
      P03divisor: recruit.P03divisor || 1,
      P04divisor: recruit.P04divisor || 1,
      P05divisor: recruit.P05divisor || 1,
      P06divisor: recruit.P06divisor || 1,
      P07divisor: recruit.P07divisor || 1,
      P08divisor: recruit.P08divisor || 1,
      P09divisor: recruit.P09divisor || 1,
      P10divisor: recruit.P10divisor || 1,
      P11divisor: recruit.P11divisor || 1,
    };

    // Store raw values for editable parameter fields
    for (let i = 1; i <= 11; i++) {
      const basePId = `P${i.toString().padStart(2, "0")}`;
      // Logic for determining max j based on i (consistent with P column generation)
      // P01 has _0 to _4 (5 items, j max 4)
      // P07 has _0 to _2 (3 items, j max 2)
      // Others have _0 to _1 (2 items, j max 1)
      const maxJ = i === 1 ? 4 : i === 7 ? 2 : 1;
      for (let j = 0; j <= maxJ; j++) {
        const pFieldKey = `${basePId}_${j}`;
        analyticsSpecificData[`raw_${pFieldKey}`] = recruit[pFieldKey] || 0;
      }
    }

    return {
      ...baseData,
      ...analyticsSpecificData,
    };
  });
};

/**
 * AnalyticsTable Component - A wrapper for BaseTable with specific configurations for analytics
 * This table supports editable parameter columns
 *
 * @param {Object} props - Component props
 * @param {Object} props.filter - Filter state
 * @param {boolean} props.columnsVisible - Whether all columns are visible
 * @param {boolean} props.headersRotated - Whether headers are rotated
 * @param {boolean} props.dataEncrypted - Whether data is encrypted
 * @param {Object} props.columnVisibility - Column visibility state
 * @param {function} props.setColumnVisibility - Function to set column visibility
 * @param {Array} props.pinnedColumns - Pinned columns
 * @param {function} props.setPinnedColumns - Function to set pinned columns
 * @param {Array} props.sorting - Sorting configuration
 * @param {function} props.setSorting - Function to set sorting
 * @param {Object} props.filterOptions - Filter options
 * @param {Object} props.setterFunctions - Filter setter functions
 * @param {Array} props.columns - Optional array of column IDs to include
 */
const AnalyticsTable = ({
  filter,
  columnsVisible,
  headersRotated = true,
  dataEncrypted = false,
  columnVisibility,
  setColumnVisibility,
  pinnedColumns = [],
  setPinnedColumns,
  sorting,
  setSorting,
  filterOptions = {},
  setterFunctions = {},
  columns = [],
  dropdownData = {},
}) => {
  // Default columns if none provided
  const defaultColumns = [
    "actions",
    "rating",
    "status",
    "code",
    "name",
    "company",
    "team",
    "department",
    "position",
  ];
  const tableId = "analytics-table";
  // Detect dynamic columns from the column specification
  const dynamicColumns = useMemo(() => {
    const pColumns = [];
    const fColumns = [];

    columns.forEach((col) => {
      if (col.startsWith("P")) {
        pColumns.push(col);
      } else if (col.startsWith("F")) {
        fColumns.push(col);
      }
    });

    return [...pColumns, ...fColumns];
  }, [columns]);

  const { processDataWithFormulas: memoizedFormatData } = useProcessedTableData(
    formatAnalyticsTableData
  );

  // Build the table configuration
  const tableConfig = useMemo(() => {
    // If columns prop is provided, use it, otherwise use default columns
    const columnsToUse = columns.length > 0 ? columns : defaultColumns;

    // Add any dynamic P or F columns that might be missing
    const allDynamicColumnKeys = Object.keys(generateDynamicColumns());
    dynamicColumns.forEach((dynamicCol) => {
      if (
        allDynamicColumnKeys.includes(dynamicCol) &&
        !columnsToUse.includes(dynamicCol)
      ) {
        columnsToUse.push(dynamicCol);
      }
    });

    // Build the table with parameter columns marked as editable
    return buildTable(
      columnsToUse,
      {
        columnVisibility,
        editableParameterColumns: true, // Flag to indicate parameter columns should be editable
      },
      tableId
    );
  }, [columns, defaultColumns, dynamicColumns, columnVisibility]);

  // Initial column visibility based on provided columns
  const initialColumnVisibility = useMemo(() => {
    const visibility = {};
    const columnsToUse = columns.length > 0 ? columns : defaultColumns;

    // Set all specified columns to visible
    columnsToUse.forEach((col) => {
      visibility[col] = true;
    });

    // Merge with any provided columnVisibility
    return { ...visibility, ...columnVisibility };
  }, [columns, defaultColumns, columnVisibility]);

  // Additional props specific to AnalyticsTable for passing to BaseTable
  const analyticsProps = {
    isAnalyticsTable: true, // Flag to indicate this is an analytics table
    allowParameterEditing: true, // Flag to allow editing parameter columns
    // Pass filter and sorting to BaseTable if it handles its own data fetching
    filter: filter,
    sorting: sorting,
  };

  return (
    <div className="analytics-table-wrapper">
      <TableProvider
        tableId={tableId}
        initialVisibility={initialColumnVisibility}
        initialColumns={columns.length > 0 ? columns : defaultColumns}
        externalColumnVisibility={columnVisibility} // Pass external visibility
        externalSetColumnVisibility={setColumnVisibility} // Pass setter for external visibility
      >
        <BaseTable
          tableConfig={tableConfig}
          formatData={memoizedFormatData} // Use the new memoized formatter
          tableId={tableId}
          columnOrder={columns.length > 0 ? columns : defaultColumns}
          dropdownData={dropdownData}
          setterFunctions={setterFunctions}
          {...analyticsProps}
          className={tableId}
        />
      </TableProvider>
    </div>
  );
};

export default AnalyticsTable;
